/**
 * usePillInvitePrompt Hook
 *
 * Prompts the user to send a calendar invite when a family member pill is tapped.
 * Uses Alert.alert for native prompt UI.
 */

import { useCallback } from 'react';
import { Alert } from 'react-native';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { PillInviteManager } from '@/lib/managers/PillInviteManager';

type UsePillInvitePromptOptions = {
  calendarMappings: CalendarMemberMapping[];
  calendars: DeviceCalendar[];
  onConfirmInvite: (familyMember: FamilyMember, email: string) => void;
};

export function usePillInvitePrompt({
  calendarMappings,
  calendars,
  onConfirmInvite,
}: UsePillInvitePromptOptions) {
  const promptForInvite = useCallback((familyMember: FamilyMember) => {
    console.log('[usePillInvitePrompt] promptForInvite called for:', familyMember.name, familyMember.id);
    console.log('[usePillInvitePrompt] calendarMappings:', JSON.stringify(calendarMappings));
    console.log('[usePillInvitePrompt] calendars:', calendars.map(c => ({ id: c.id, ownerAccount: c.ownerAccount })));

    const emails = PillInviteManager.resolveEmails(
      familyMember.id,
      calendarMappings,
      calendars,
    );
    console.log('[usePillInvitePrompt] resolved emails:', emails);

    if (emails.length === 0) return;

    if (emails.length === 1) {
      Alert.alert(
        'Send calendar invite?',
        `Send a calendar invite to ${familyMember.name} (${emails[0]})?`,
        [
          { text: 'No', style: 'cancel' },
          { text: 'Yes', onPress: () => onConfirmInvite(familyMember, emails[0]) },
        ],
      );
    } else {
      const emailButtons = emails.map(email => ({
        text: email,
        onPress: () => onConfirmInvite(familyMember, email),
      }));
      Alert.alert(
        `Which email for ${familyMember.name}?`,
        'Choose which email to send the calendar invite to:',
        [...emailButtons, { text: 'Cancel', style: 'cancel' as const }],
      );
    }
  }, [calendarMappings, calendars, onConfirmInvite]);

  return { promptForInvite };
}
